import { useState } from 'react';
import { Post } from '@/features/social/hooks/usePosts';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { ExpandableText } from '@/components/ui/ExpandableText';
import { PremiumBadge } from '@/components/ui/PremiumBadge';
import { LikeButton } from './LikeButton';
import { CommentButton } from './CommentButton';
import { CommentInput } from './CommentInput';
import { CommentItem } from './CommentItem';
import { PendingCommentItem } from './PendingCommentItem';
import { usePostComments } from '@/features/social/hooks/usePostComments';
import { Button } from '@/components/ui/button';
import { Loader2, MoreHorizontal, Trash2, X } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { useAdminPermissions } from '@/shared/hooks/useAdminPermissions';
import { useAdminDeletePost } from '@/shared/hooks/useAdminDeletePost';
import AdminDeletePostModal from '@/components/modals/AdminDeletePostModal';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from '@/components/ui/alert-dialog';
import { postsRepository } from '@/features/social/repositories/supabasePostsRepository';
import { useToast } from '@/components/ui/use-toast';
import { CustomDropdownMenu } from '@/components/ui/CustomDropdownMenu';
import { Link } from 'react-router-dom';
import { getInitials } from '@/utils/avatarUtils';
import { formatShortDistance } from '@/utils/relativeTime';

interface PostCardProps {
  post: Post;
  onDelete?: (postId: string) => void;
}

const beltFilters: Record<string, string> = {
  white: '',
  blue: 'invert(1) sepia(1) saturate(10) hue-rotate(200deg) brightness(0.8)',
  purple: 'invert(1) sepia(1) saturate(10) hue-rotate(270deg) brightness(0.8)',
  brown: 'invert(1) sepia(1) saturate(10) hue-rotate(10deg) brightness(0.6)',
  black: 'invert(1) sepia(1) saturate(10) brightness(0.1)',
};


export const PostCard = ({ post, onDelete }: PostCardProps) => {
  const { user } = useAuth();
  const { t } = useI18n();
  const { isAdmin } = useAdminPermissions();
  const isOwnPost = user?.id === post.user_id;
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAdminDeleteModal, setShowAdminDeleteModal] = useState(false);
  const adminDeletePost = useAdminDeletePost();
  const [deleting, setDeleting] = useState(false);

  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
  };

  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    comments,
    commentCount,
    isLoading,
    hasMore,
    pendingComments,
    loadMoreComments,
    addComment,
    retryComment,
    removePendingComment,
    canComment,
    deleteComment
  } = usePostComments(post.id);

  const belt = (post.author.belt_rank || 'white').toLowerCase();
  const beltFilter = beltFilters[belt] || '';

  const handleCommentClick = () => {
    if (!canComment) {
      window.location.href = '/?tab=signin';
      return;
    }
    setIsCommentsOpen(!isCommentsOpen);
  };

  const handleSubmitComment = async (content: string) => {
    setIsSubmitting(true);
    try {
      await addComment(content);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetryComment = async (pendingComment: any) => {
    await retryComment(pendingComment);
  };

  // Exclusão de post
  const handleDeletePost = async () => {
    setDeleting(true);
    try {
      await postsRepository.deletePost(post.id);
      setShowDeleteDialog(false);
      if (onDelete) onDelete(post.id);
    } catch (error) {
      toast({
        title: t('notifications.toasts.post_delete_failed'),
        description: t('notifications.toasts.post_delete_failed_description'),
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  // Admin deletion
  const handleAdminDelete = async (reason: string) => {
    try {
      await adminDeletePost.mutateAsync({
        postId: post.id,
        reason: reason
      });
      setShowAdminDeleteModal(false);
      if (onDelete) onDelete(post.id);
    } catch (error) {
      console.error('Error deleting post as admin:', error);
    }
  };

  return (
    <div className="py-3.5 px-4 w-full relative">
      {/* Botão de 3 pontos (para o autor ou admin) */}
      {(isOwnPost || isAdmin) && (
        <div className="absolute top-3 right-0 z-10">
          <CustomDropdownMenu
            trigger={
              <span className="ml-2 p-1 rounded-full focus:outline-none cursor-pointer transition-colors inline-flex items-center justify-center">
                <MoreHorizontal className="h-5 w-5 text-gray-400" />
              </span>
            }
            items={[
              ...(isOwnPost ? [{
                label: t('posts.delete_post'),
                icon: <Trash2 className="h-4 w-4 mr-2" />,
                onClick: () => setShowDeleteDialog(true),
                className: "text-white"
              }] : []),
              ...(isAdmin && !isOwnPost ? [{
                label: t('admin.delete_as_admin'),
                icon: <Trash2 className="h-4 w-4 mr-2" />,
                onClick: () => setShowAdminDeleteModal(true),
                className: "text-red-400"
              }] : [])
            ]}
          />
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="w-[90vw] max-w-sm bg-white rounded-2xl p-6 shadow-xl">
          {/* Close button */}
          <button
            onClick={() => setShowDeleteDialog(false)}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label="Fechar"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>

          <div className="space-y-5">
            {/* Icon + title + description */}
            <div className="text-center space-y-4 pt-1">
              <div className="flex justify-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <div>
                <AlertDialogTitle className="text-[17px] font-semibold text-gray-900 mb-1">
                  {t('posts.confirm_deletion')}
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] text-gray-500 leading-relaxed">
                  {t('posts.delete_confirmation_message')}
                </AlertDialogDescription>
              </div>
            </div>

            {/* Buttons — stacked, full width, pill */}
            <AlertDialogFooter className="!flex-col !justify-start !space-x-0 gap-2">
              <AlertDialogAction
                onClick={handleDeletePost}
                disabled={deleting}
                className="h-11 w-full rounded-full border-0 font-medium text-white bg-sossiety-red hover:bg-sossiety-red-hover disabled:opacity-50"
              >
                {deleting ? t('posts.deleting') : t('common.delete')}
              </AlertDialogAction>
              <AlertDialogCancel className="mt-0 h-11 w-full rounded-full border-0 font-medium bg-[#F0F0F0] text-gray-900 hover:bg-gray-200">
                {t('common.cancel')}
              </AlertDialogCancel>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Author Info */}
      <div className="flex items-start space-x-3 mb-2">
        <Link to={post.author.username ? `/profile/${post.author.username}` : '#'} className="flex items-start space-x-3 group" tabIndex={post.author.username ? 0 : -1} aria-disabled={!post.author.username}>
          <div className="flex-shrink-0">
            <FramedAvatar
              src={post.author.profile_picture_url}
              alt={post.author.full_name}
              initials={getInitials(post.author.full_name, 'A')}
              frameSize={{ w: 44, h: 48 }}
              beltRank={post.author.belt_rank}
              username={post.author.username}
              isManual
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-white text-sm min-w-0 truncate group-hover:underline">{post.author.full_name || 'Anonymous'}</span>
              {post.author.is_premium && <PremiumBadge size="xs" />}
              <span className="text-xs text-gray-400 flex-shrink-0 ml-auto">• {formatShortDistance(new Date(post.created_at))}</span>
            </div>
            <div className="text-xs text-gray-400 font-light">{post.author.academy_name || 'No academy'}</div>
          </div>
        </Link>
      </div>
      
      {/* Content */}
      <div className="mb-3 mt-1">
        <ExpandableText
          text={post.content}
          maxLength={280}
          className="text-[14px] text-gray-100 leading-[1.5] post-content"
          buttonText={{
            expand: 'View more',
            collapse: 'View less'
          }}
        />
      </div>

      {/* Media */}
      {post.media_url && post.media_type && (
        <div className="mt-2 mb-3 rounded-xl overflow-hidden">
          {post.media_type === 'video' ? (
            <VideoPlayer src={post.media_url} />
          ) : (
            <img src={post.media_url} alt="Post media" className="w-full object-cover max-h-80 rounded-xl" />
          )}
        </div>
      )}
      
      {/* Actions */}
      <div className="flex items-center space-x-6 pt-1">
        <LikeButton
          postId={post.id}
          className="text-gray-200 hover:text-sossiety-red-600"
        />
        <CommentButton
          commentCount={commentCount}
          onClick={handleCommentClick}
          className="text-gray-200 hover:text-sossiety-red-600"
          disabled={!canComment}
          filled={isCommentsOpen}
        />
      </div>

      {/* Comments Section */}
      {isCommentsOpen && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          {/* Comment Input */}
          {canComment && (
            <div className="mb-4">
              <CommentInput
                onSubmit={handleSubmitComment}
                isSubmitting={isSubmitting}
              />
            </div>
          )}

          {/* Comments List */}
          <div className="space-y-2">
            {/* Pending Comments */}
            {pendingComments.map((pendingComment) => (
              <PendingCommentItem
                key={pendingComment.id}
                comment={pendingComment}
                onRetry={() => handleRetryComment(pendingComment)}
                onRemove={() => removePendingComment(pendingComment.id)}
              />
            ))}

            {/* Regular Comments */}
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onDelete={deleteComment}
              />
            ))}

            {/* Loading State */}
            {isLoading && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            )}

            {/* Empty State */}
            {!isLoading && comments.length === 0 && pendingComments.length === 0 && (
              <div className="text-center py-4">
                <p className="text-gray-400 text-sm">
                  This publication has no comments yet. Be the first to comment!
                </p>
              </div>
            )}

            {/* Load More Button */}
            {hasMore && !isLoading && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadMoreComments}
                  className="border-white text-white bg-white/5 hover:bg-white/10 rounded-full px-6 font-semibold"
                >
                  View more comments
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin Delete Modal */}
      <AdminDeletePostModal
        isOpen={showAdminDeleteModal}
        onClose={() => setShowAdminDeleteModal(false)}
        onConfirm={handleAdminDelete}
        isLoading={adminDeletePost.isPending}
      />
    </div>
  );
};
